import { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor, SortDescriptor, toDataSourceRequestString } from "@progress/kendo-data-query";
import { Grid, GridColumn, GridFilterChangeEvent, GridPageChangeEvent, GridPagerSettings, GridRowClickEvent, GridSortChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { protectedResources } from "../../authConfig";
import { useFetchWithMsal } from "../../hooks/useFetchWithMsal";
import { LoadPage } from "../LoadPage";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import * as React from "react";

//props.filter should look something like this
//[{
//    field: "LabId", //field should be capatilized like it is in the c# Model
//    operator: "eq",
//    value: props.id,
//}]

//props.intialSort should look something like this
//[{
//    field: "Id",
//    dir: "desc"
//}]

//props.overrideableFilter is a filter that should be ignored if the user filters on that fieild
//  For example, we don't want to show voided reports by default, but if an admin user wants to see Voided Final, or Voided Review items they should be able to find them

export interface ButtonHelper {
    buttonText: string,
    onClick: Function
}

interface BaseGridProps {
    verb: string,
    pageTitle: string,
    endpoint: string,
    redirectUrl?: string,
    filter?: FilterDescriptor[],
    overrideableFilter?: FilterDescriptor,
    intialSort?: Array<SortDescriptor>,
    onRowDoubleClick?: Function,
    button?: ButtonHelper[],
    defaultPageSize?: number,
    excelFunction?: Function,
    children?: React.ReactNode,
}

export const BaseGrid = (props: BaseGridProps) => {
    const navigate = useNavigate();
    const [data, setData] = useState([] as any[]);
    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const defaultSize = props.defaultPageSize ? props.defaultPageSize : 10;

    const [skip, setSkip] = useState(0);
    const [take, setTake] = useState(defaultSize);

    const [sort, setSort] = useState(props.intialSort ?
        props.intialSort :
        new Array<SortDescriptor>());
    const [lastSort, setLastSort] = useState(sort);
    const [filter, setFilter] = useState((props.filter ?
        {
            logic: "and",
            filters: props.filter
        }
        : {
            logic: "and",
            filters: []
        }) as CompositeFilterDescriptor);
    const [lastSearchFilter, setLastSearchFilter] = useState(filter);

    const filterChange = (e: GridFilterChangeEvent) => {
        setSkip(0);
        setFilter(e.filter);
    }

    const sortChange = (_sort: GridSortChangeEvent) => {
        setSort(_sort.sort);
    }

    const pageChange = (ePageChange: GridPageChangeEvent) => {
        setTake(ePageChange.page.take);
        setSkip(ePageChange.page.skip);
    }

    const getDataSourceRequestState = (_skip?: number, _take?: number) => {
        let queryFilter = {
            logic: "and",
            filters: [] as any[]
        };
        if (filter?.filters) {
            filter.filters.forEach((x: any) => {
                if (x.operator === "dateRange") {
                    queryFilter.filters.push({
                        field: x.field,
                        operator: "gte",
                        value: x.value[0]
                    });
                    queryFilter.filters.push({
                        field: x.field,
                        operator: "lte",
                        value: x.value[1]
                    });
                } else {
                    queryFilter.filters.push(x);
                }
            })
        }

        if (props.overrideableFilter) {
            const overrideFilter = queryFilter.filters.findIndex((x: any) => x.field === props.overrideableFilter?.field);
            if (overrideFilter === -1) {
                queryFilter.filters.push(props.overrideableFilter);
            } 
        } 

        return {
            skip: _skip ? _skip : skip,
            take: _take ? _take : take,
            filter: queryFilter,
            sort: sort,
        } as DataSourceRequestState;
    }

    const getData = () => {
        execute(props.verb, props.endpoint + "?" + toDataSourceRequestString(getDataSourceRequestState())).then((result: any) => {
            if (result?.status === 404) {
                setData([]);//If the request returns a 404 we assume that everything was filtered out, so we want to display a blank table
            } else {
                setData(result);
            }
        });
    }

    useEffect(() => {
        if (!data) {
            getData();
        }
    }, [execute]);

    useEffect(() => {
        getData();
    }, [skip, take]);

    useEffect(() => {
        const delayedFilterSearch = setTimeout(() => {
            if (lastSearchFilter !== filter || lastSort !== sort) {
                setLastSearchFilter(filter);
                setLastSort(sort);
                getData();
            }
        }, 500);

        return () => clearTimeout(delayedFilterSearch);
    }, [filter, sort]);

    const onRowDoubleClick = (e: GridRowClickEvent) => {
        if (props.redirectUrl) {
            navigate(props.redirectUrl + e.dataItem.id);
        } else if (props.onRowDoubleClick) {
            props.onRowDoubleClick?.(e.dataItem.id);
        }
    }

    const redirectCell = (data: any) => {
        return (
            <td onClick={() => {
                navigate(props.redirectUrl + data.dataItem.id);
            }}>
                <span className="k-icon k-font-icon k-i-hyperlink-open"></span>
            </td>
        )
    }

    //#region Export Excel
    const _export = React.useRef<ExcelExport | null>(null);
    const pageTitle = props.pageTitle.replace(/\s/g, '')
    const fileName = pageTitle + "_Export"

    const modifiedStatus = (excelReults: any[]) => excelReults.map((item: any) => {
        return props.excelFunction?.(item)
    });

    const getExcel = () => {
        execute(props.verb, props.endpoint + "?" + toDataSourceRequestString(getDataSourceRequestState(0, 10000))).then((result: any) => {
            if (result?.status !== 404 && _export.current !== null) {
                _export.current.save((props.excelFunction !== undefined) ? modifiedStatus(result.data) : result);
            }
        });
    }
    //#endregion

    return (
        <LoadPage isLoading={isLoading}>
            <GridToolbar>
                {props.button &&
                    <>
                        {props.button?.map((x: any) => {
                            return <button onClick={() => x?.onClick(getDataSourceRequestState(0, 10000))}
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary">{x.buttonText}</button>
                        })}
                    </>}
                <button title="Export Excel" className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-secondary"
                    onClick={getExcel}>Export to Excel</button>
            </GridToolbar>
            <ExcelExport fileName={fileName} ref={_export}>
                <Grid
                    pageable={{
                        buttonCount: 3,
                        pageSizes: [10, 25, 50, 75, 100],
                        pageSizeValue: take,
                    } as GridPagerSettings}
                    resizable={true}
                    sortable={true}
                    sort={sort}
                    onSortChange={sortChange}
                    onPageChange={pageChange}
                    skip={skip} take={take}
                    reorderable={true}
                    data={data}
                    filterable={true}
                    filter={filter}
                    onFilterChange={filterChange}
                    onRowDoubleClick={onRowDoubleClick}>
                    {props.redirectUrl &&
                        <GridColumn width="35px" cell={redirectCell} filterable={false} sortable={false} />}
                    {props.children}
                </Grid>
            </ExcelExport>
        </LoadPage>
    );
}

export default BaseGrid;